import React, { useCallback, useEffect, useMemo } from 'react'
import { Flex, Stack, Switch, Textarea, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { createChangelog, updateChangelog } from 'src/apis/changelogAPI'
import CustomButton from 'src/components/CustomComponents/CustomButton'
import CustomModal from 'src/components/CustomComponents/CustomModal'
import { useModal } from 'src/context/ModalContext'
import { ChangelogData } from 'src/interfaces'
import { capitalizeFirstLetter } from 'src/utils/stringUlti'

interface ModalCreateChangelogProps {
	open: boolean
	data: ChangelogData
	onClose: () => void
	handleGetData: () => void
	pagination: {
		pageIndex: number
		pageSize: number
	}
	handleChangePage: (_value: number) => void
	[key: string]: any
}

function ModalCreateChangelog({
	open,
	onClose,
	handleGetData,
	pagination,
	handleChangePage,
	data,
}: ModalCreateChangelogProps) {
	const form = useForm({
		initialValues: {
			title: '',
			description: '',
			version: '',
			is_published: false,
		},
		validate: {
			title: (value) => {
				if (!value.trim()) return 'Title cannot be empty'
				if (value.length > 100) return 'Title must be 1-100 characters long'
				return null
			},
			description: (value) => {
				if (value && value.length > 2000)
					return 'Description must be less than or equal to 2000 characters'
				return null
			},
			version: (value) => {
				if (value && value.length > 50)
					return 'Version must be less than or equal to 50 characters'
				return null
			},
		},
		validateInputOnBlur: true,
		validateInputOnChange: true,
	})

	const { openModal, closeModal, openError } = useModal()

	const text = useMemo(() => {
		const { id } = data || {}
		return id ? 'update' : 'create'
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(data)])

	const handleCreateOrUpdate = useCallback(async ({ body, id = null }) => {
		try {
			const res = await (id ? updateChangelog(id, body) : createChangelog(body))
			if (res?.data?.code === 200) {
				if (id || pagination.pageIndex === 1) {
					await handleGetData()
				} else {
					await handleChangePage(1)
				}
				onClose()
				openModal({
					type: 'success',
					label: `Changelog has been ${text} successfully`,
					handleClose: () => {
						onClose()
					},
				})
			}
		} catch (error) {
			openError(error)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const handleSubmit = useCallback(async (values) => {
		const body = {
			title: values.title.trim(),
			description: values.description?.trim() ? values.description.trim() : null,
			version: values.version?.trim() ? values.version.trim() : null,
			is_published: values.is_published,
		}

		openModal({
			type: 'confirm',
			label: `Do you want to ${text} changelog ?`,
			handleClose: () => closeModal(),
			handleConfirm: () => {
				handleCreateOrUpdate({ body, id: data?.id || null })
			},
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (data) {
			const {
				title = '',
				description = '',
				version = '',
				is_published = false,
			} = data
			form.setValues({
				title,
				description: description || '',
				version: version || '',
				is_published,
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(data)])

	return (
		<CustomModal opened={open} onClose={onClose}>
			<form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
				<Stack
					spacing={10}
					sx={{
						paddingTop: 10,
						paddingBottom: 10,
						height: 'fit-content',
					}}
				>
					<Flex sx={{ fontSize: 25, fontWeight: 700 }} justify="center">
						{capitalizeFirstLetter(text)} changelog
					</Flex>
					<TextInput
						autoComplete="off"
						withAsterisk
						key="title"
						label="Title"
						name="title"
						placeholder="Version 1.0.0"
						maxLength={100}
						{...form.getInputProps('title')}
					/>
					<Textarea
						autoComplete="off"
						key="description"
						label="Description"
						name="description"
						placeholder="Release notes"
						maxLength={2000}
						autosize
						minRows={4}
						{...form.getInputProps('description')}
					/>
					{/* <TextInput
						autoComplete="off"
						key="version"
						label="Version"
						name="version"
						placeholder="1.0.0"
						maxLength={50}
						{...form.getInputProps('version')}
					/> */}
					<Switch
						label="Published"
						onLabel="Yes"
						offLabel="No"
						radius="sm"
						size="md"
						checked={form.values.is_published}
						onChange={(event) =>
							form.setFieldValue('is_published', event.currentTarget.checked)
						}
					/>
					<CustomButton variant="outlined" type="submit" sx={{ marginTop: '10px' }}>
						{text}
					</CustomButton>
				</Stack>
			</form>
		</CustomModal>
	)
}

export default React.memo(ModalCreateChangelog)
